/**
 * feat/branch-write-require-active — hook compartido para las ESCRITURAS scopeadas
 * por sucursal. El backend ahora EXIGE `branchId` explícito en cada escritura por
 * sucursal (400 si falta); la sucursal destino es la ACTIVA DE LA SESIÓN.
 *
 * Regla (idéntica al modelo de InsumosView, BoutiqueViews.tsx):
 *   - sessionBranchId = user.branch.activeBranchId ?? homeBranchId ?? ''  (la del candado
 *     si el usuario está atado, si no la elegida en la barra superior; '' = "Todas").
 *   - writeBranchId = sessionBranchId || (con 1 sola sucursal, la única). En vista
 *     consolidada ("Todas") con >1 sucursal NO hay destino único → null.
 *   - needsChoice = !writeBranchId → el disparador se DESHABILITA con una pista que
 *     apunta a la barra superior (elegir una sucursal concreta).
 *
 * Reusa `/branches` (activas) como el resto de las herramientas por sucursal.
 */
import { useEffect, useState } from 'react';
import { api } from './api';
import { useAuth } from '../auth/AuthProvider';
import type { BranchRow } from '../components/BranchControls';

export interface ActiveWriteBranch {
  /** Sucursales ACTIVAS del tenant. */
  branches: BranchRow[];
  /** ¿El tenant tiene más de una sucursal activa? (gatea los affordances). */
  multiBranch: boolean;
  /** Sucursal destino de la escritura, o null si hay que elegir en la barra superior. */
  writeBranchId: string | null;
  /** true = consolidado ("Todas") con >1 sucursal → sin destino único; hay que elegir. */
  needsChoice: boolean;
}

export function useActiveWriteBranch(): ActiveWriteBranch {
  const { user } = useAuth();
  const [branches, setBranches] = useState<BranchRow[]>([]);

  useEffect(() => {
    api<BranchRow[]>('/branches')
      .then((b) => setBranches(Array.isArray(b) ? b.filter((x) => x.isActive) : []))
      .catch(() => setBranches([]));
  }, []);

  const sessionBranchId = user?.branch?.activeBranchId ?? user?.branch?.homeBranchId ?? '';
  const writeBranchId = sessionBranchId || (branches.length === 1 ? branches[0].id : null);

  return {
    branches,
    multiBranch: branches.length > 1,
    writeBranchId,
    needsChoice: !writeBranchId,
  };
}
